import { useEffect, useMemo } from 'react'
import { FilterList } from '@mui/icons-material'
import { Badge, Box, Button, IconButton, Stack, Tooltip } from '@mui/material'
import useOverlay from '@/hooks/useOverlay'
import useSessionStorage from '@/hooks/useSessionStorage'
import { ByTimeMetricReport } from '@/types/workflow-metric-reports'
import { Dialog, DialogContent, DialogFooter } from '@/components/dialog'
import useGetWorkflowStateMenuItems from '@/components/workflow-state-dropdown/useGetWorkflowStateMenuItems'
import { useSelectedWorkflowContext } from '@/components/workflows/SelectedWorkflowProvider'
import { useCurrentAccountContext } from '../accounts/CurrentAccountProvider'
import { useProjectContext } from '../project-tables/ProjectProvider'

type UseWorkflowSelectedStateIdsOptions = {
  report?: ByTimeMetricReport
  key: string
}

type MetricsStatesFilterProps = {
  allStateIds?: string[]
  selectedStateIds?: string[]
  setSelectedStateIds: (
    v:
      | string[]
      | ((val: string[] | undefined) => string[] | undefined)
      | undefined,
  ) => void
}

export function useWorkflowSelectedStateIds({
  key,
  report,
}: UseWorkflowSelectedStateIdsOptions) {
  const { project } = useProjectContext()
  const { selectedWorkflow } = useSelectedWorkflowContext()

  const stateIds = useMemo(() => {
    return (
      report?.reports[0].reports.map((r) => r.workflow_state_id) || undefined
    )
  }, [report])

  const { currentAccount } = useCurrentAccountContext()

  const [selectedStateIds, setSelectedStateIds] = useSessionStorage(
    `card-metrics-${key}-states-ids=${currentAccount.id}-${project.id}-${selectedWorkflow.id}`,
    stateIds,
  )

  useEffect(() => {
    if (stateIds && !selectedStateIds) {
      setSelectedStateIds(stateIds)
    }
  }, [stateIds, selectedStateIds, setSelectedStateIds])

  const filteredStateIds = useMemo(
    () =>
      selectedStateIds?.length === stateIds?.length
        ? undefined
        : selectedStateIds,
    [selectedStateIds, stateIds],
  )

  return {
    allStateIds: stateIds,
    filteredStateIds,
    selectedStateIds,
    setSelectedStateIds,
  }
}

export default function MetricsStatesFilter({
  allStateIds,
  selectedStateIds,
  setSelectedStateIds,
}: MetricsStatesFilterProps) {
  const statesFilterOverlay = useOverlay()
  const { selectedWorkflow } = useSelectedWorkflowContext()

  const workflowStates = useMemo(
    () =>
      selectedWorkflow.workflow_states?.filter(({ id, code }) =>
        allStateIds ? allStateIds.includes(id) : code !== 'processing',
      ) || [],
    [selectedWorkflow.workflow_states, allStateIds],
  )

  const workflowStateMenuItems = useGetWorkflowStateMenuItems({
    workflowStates,
    multiSelect: true,
    typographyVariant: 'body1',
    selectedWorkflowStateIds: selectedStateIds,
    onChange: (state, isSelected) => {
      setSelectedStateIds((prev: string[] | undefined) => {
        const updated = prev ? prev : []
        return isSelected
          ? [...updated, state.id]
          : updated.filter((id: string) => id !== state.id)
      })
    },
  })

  const isFilteringStates =
    allStateIds &&
    allStateIds.length > 0 &&
    selectedStateIds?.length !== allStateIds.length

  return (
    <>
      <Tooltip title="Filter by Workflow States" enterDelay={1000}>
        <Stack direction="row">
          <Badge
            badgeContent={isFilteringStates && selectedStateIds?.length}
            invisible={!isFilteringStates}
            showZero
            color="secondary"
          >
            <IconButton
              onClick={statesFilterOverlay.open}
              disabled={!allStateIds}
              size="small"
              color={isFilteringStates ? 'secondary' : 'default'}
            >
              <FilterList fontSize="small" />
            </IconButton>
          </Badge>
        </Stack>
      </Tooltip>
      <Dialog title="States" maxWidth="xs" {...statesFilterOverlay}>
        <DialogContent>
          <Button
            disabled={!allStateIds}
            variant="text"
            onClick={() => {
              if (allStateIds?.length === selectedStateIds?.length) {
                setSelectedStateIds([])
              } else {
                setSelectedStateIds(allStateIds)
              }
            }}
            sx={{
              mb: 1,
              ml: -0.5,
            }}
          >
            {allStateIds?.length === selectedStateIds?.length
              ? 'Deselect'
              : 'Select'}{' '}
            All
          </Button>
          <Box sx={{ mx: '3px' }}>{workflowStateMenuItems}</Box>
        </DialogContent>
        <DialogFooter>
          <Button onClick={statesFilterOverlay.close} variant="text">
            Close
          </Button>
        </DialogFooter>
      </Dialog>
    </>
  )
}
